// Split out of InstanceDetailPage.tsx, then migrated onto components/ui.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useDeleteInstance, useLifecycle, useResetAdminPassword } from '../../hooks/useInstances'
import { useAuthStore } from '../../store/authStore'
import { metaApi, usersApi, instancesApi } from '../../api/client'
import type { Instance } from '../../api/client'
import { CopyButton, extractApiError } from './shared'
import {
  Alert,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  Field,
  Input,
  Modal,
  Select,
} from '../../components/ui'

export function ResetAdminPasswordSection({ instance }: { instance: Instance }) {
  const { t } = useTranslation()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [resetError, setResetError] = useState('')
  const [resetSuccess, setResetSuccess] = useState(false)
  const resetMutation = useResetAdminPassword(instance.id)

  const passwordsMatch = newPassword === confirmPassword
  const passwordValid = newPassword.length >= 8
  // `isPending` moved out of the gate and into the Button's `loading`, which
  // already does `disabled={disabled || loading}`.
  const canSubmit = passwordValid && passwordsMatch && instance.status === 'running'

  const handleReset = () => {
    setResetError('')
    setResetSuccess(false)
    resetMutation.mutate(
      { newPassword, confirmPassword },
      {
        onSuccess: () => {
          setResetSuccess(true)
          setNewPassword('')
          setConfirmPassword('')
        },
        onError: (e) => setResetError(extractApiError(e) ?? t('detail.overview.resetError')),
      },
    )
  }

  return (
    <Card tone="warning">
      <CardHeader
        title={t('detail.overview.resetPassword')}
        description={t('detail.overview.resetPasswordHint')}
        className="mb-4"
      />
      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">{t('detail.overview.login')}</span>
          <div className="flex items-center gap-2">
            <span className="font-mono">admin</span>
            <CopyButton text="admin" />
          </div>
        </div>
        {/* The two inputs carried only a placeholder before; Field wires a real
            label to each, which is what the primitive is for. */}
        <Field label={t('detail.overview.newPassword')}>
          {(props) => (
            <Input
              {...props}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('detail.overview.confirmPassword')}>
          {(props) => (
            <Input
              {...props}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              invalid={Boolean(confirmPassword) && !passwordsMatch}
            />
          )}
        </Field>
        {resetError && <Alert tone="danger">{resetError}</Alert>}
        {resetSuccess && <Alert tone="success">{t('detail.overview.resetSuccess')}</Alert>}
        <Button
          variant="warning"
          fullWidth
          onClick={handleReset}
          disabled={!canSubmit}
          loading={resetMutation.isPending}
        >
          {resetMutation.isPending ? t('detail.overview.resetting') : t('detail.overview.resetBtn')}
        </Button>
        {instance.status !== 'running' && (
          <p className="text-xs text-gray-400 text-center">{t('detail.overview.resetRequiresRunning')}</p>
        )}
      </div>
    </Card>
  )
}

export function DangerTab({ instance }: { instance: Instance }) {
  const [confirmName, setConfirmName] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [transferUserId, setTransferUserId] = useState('')
  const [transferSuccess, setTransferSuccess] = useState(false)
  const [transferError, setTransferError] = useState('')
  const [showRecreateConfirm, setShowRecreateConfirm] = useState(false)
  const [recreateError, setRecreateError] = useState('')
  const [recreateSuccess, setRecreateSuccess] = useState(false)
  const lifecycle = useLifecycle()
  const deleteMutation = useDeleteInstance()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const role = useAuthStore((s) => s.role)
  const qc = useQueryClient()
  const { data: meta } = useQuery({ queryKey: ['meta'], queryFn: metaApi.get, staleTime: Infinity })

  const { data: allUsers = [] } = useQuery({
    queryKey: ['users', 'list-all'],
    queryFn: usersApi.listAll,
    enabled: role === 'admin',
    staleTime: 60_000,
  })

  const transferMutation = useMutation({
    mutationFn: (userId: string) => instancesApi.assignOwner(instance.id, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instances', instance.id] })
      qc.invalidateQueries({ queryKey: ['instances'] })
      setTransferUserId('')
      setTransferSuccess(true)
      setTimeout(() => setTransferSuccess(false), 3000)
    },
  })

  // A transfer that failed used to do nothing visible at all — no onError.
  const handleTransfer = () => {
    setTransferError('')
    transferMutation.mutate(transferUserId, {
      onError: (e) => setTransferError(extractApiError(e) ?? t('common.error')),
    })
  }

  const handleRecreate = () => {
    setRecreateError('')
    lifecycle.recreate.mutate(instance.id, {
      onSuccess: () => {
        setShowRecreateConfirm(false)
        setRecreateSuccess(true)
        setTimeout(() => setRecreateSuccess(false), 4000)
      },
      onError: (e) => setRecreateError(extractApiError(e) ?? t('detail.danger.recreateError')),
    })
  }

  const closeDeleteDialog = () => {
    setShowConfirm(false)
    setConfirmName('')
    setDeleteError('')
  }

  // Was `await deleteMutation.mutateAsync(...)` with no catch: a failed delete
  // navigated nowhere and said nothing, and the rejection escaped as an
  // unhandled promise. The typed-name gate is re-checked here so the request
  // cannot be sent even if the button's disabled state is ever bypassed.
  const handleDelete = () => {
    if (confirmName !== instance.name) return
    setDeleteError('')
    deleteMutation.mutate(instance.id, {
      onSuccess: () => navigate('/dashboard'),
      onError: (e) => setDeleteError(extractApiError(e) ?? t('common.error')),
    })
  }

  // Non-admin users cannot delete — show support info instead
  if (role !== 'admin') {
    return (
      <div className="space-y-4">
        <ResetAdminPasswordSection instance={instance} />
        <Card>
          <CardHeader title={t('detail.danger.title')} description={t('detail.danger.supportText')} className="mb-4" />
          {meta?.support_email && (
            /* Stays an <a>: it opens the mail client, it is not an action. */
            <a
              href={`mailto:${meta.support_email}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-50 border border-brand-200 text-brand-700 text-sm font-medium rounded-lg hover:bg-brand-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {meta.support_email}
            </a>
          )}
        </Card>
      </div>
    )
  }

  // Admins are listed too: a common cleanup flow is reassigning an instance
  // to the platform admin so the original owner can be deleted.
  const transferTargets = allUsers

  return (
    <div className="space-y-4">
      <ResetAdminPasswordSection instance={instance} />

      {/* Admin-only: Transfer Instance */}
      <Card>
        <CardHeader
          title={t('detail.danger.transferTitle')}
          description={t('detail.danger.transferHint')}
          className="mb-4"
        />
        {transferSuccess && (
          <Alert tone="success" className="mb-3">{t('detail.danger.transferSuccess')}</Alert>
        )}
        {transferError && <Alert tone="danger" className="mb-3">{transferError}</Alert>}
        <div className="flex gap-3">
          <Select
            value={transferUserId}
            onChange={(e) => setTransferUserId(e.target.value)}
            className="flex-1"
          >
            <option value="">{t('detail.danger.transferSelectUser')}</option>
            {transferTargets.map((u) => (
              <option key={u.id} value={u.id}>
                {u.email}{u.role === 'admin' ? ' (admin)' : ''}
              </option>
            ))}
          </Select>
          <Button
            onClick={handleTransfer}
            disabled={!transferUserId}
            loading={transferMutation.isPending}
            className="whitespace-nowrap"
          >
            {transferMutation.isPending ? t('detail.danger.transferring') : t('detail.danger.transferBtn')}
          </Button>
        </div>
      </Card>

      {/* Admin-only: Recreate Odoo container (rolls out label/config changes without touching data) */}
      <Card tone="warning">
        <CardHeader
          title={t('detail.danger.recreateTitle')}
          description={t('detail.danger.recreateHint')}
          className="mb-4"
        />
        {recreateSuccess && (
          <Alert tone="success" className="mb-3">{t('detail.danger.recreateSuccess')}</Alert>
        )}
        <Button
          variant="warning"
          onClick={() => { setShowRecreateConfirm(true); setRecreateError(''); setRecreateSuccess(false) }}
          disabled={lifecycle.recreate.isPending || (instance.status !== 'running' && instance.status !== 'stopped')}
        >
          {t('detail.danger.recreateBtn')}
        </Button>
      </Card>

      <Card tone="danger">
        <h3 className="text-base font-semibold text-red-700 mb-1">{t('detail.danger.title')}</h3>
        <p className="text-sm text-gray-500 mb-4"
          dangerouslySetInnerHTML={{ __html: t('detail.danger.warning', { name: instance.name }) }}
        />
        <Button variant="danger" onClick={() => setShowConfirm(true)}>
          {t('detail.danger.deleteBtn')}
        </Button>
      </Card>

      {/* The inline two-button confirm became a dialog so it names the instance
          it is about to destroy. The typed-name gate is unchanged: the confirm
          button stays disabled until the typed text matches exactly. */}
      {showRecreateConfirm && (
        <ConfirmDialog
          title={t('detail.danger.recreateTitle')}
          message={
            <>
              <span className="block font-medium text-gray-800 break-all">{instance.name}</span>
              {t('detail.danger.recreateHint')}
              {recreateError && <span className="mt-2 block text-red-600">{recreateError}</span>}
            </>
          }
          confirmLabel={lifecycle.recreate.isPending ? t('detail.danger.recreating') : t('detail.danger.recreateConfirm')}
          cancelLabel={t('detail.danger.cancel')}
          tone="primary"
          busy={lifecycle.recreate.isPending}
          onConfirm={handleRecreate}
          onCancel={() => setShowRecreateConfirm(false)}
        />
      )}

      {/* Not ConfirmDialog: its confirm button cannot be disabled, and this one
          must stay disabled until the instance name is typed exactly. */}
      {showConfirm && (
        <Modal
          onClose={closeDeleteDialog}
          title={t('detail.danger.title')}
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={closeDeleteDialog}>
                {t('detail.danger.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={confirmName !== instance.name}
                loading={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? t('detail.danger.deleting') : t('detail.danger.confirm')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-gray-600"
              dangerouslySetInnerHTML={{ __html: t('detail.danger.warning', { name: instance.name }) }}
            />
            <p className="text-sm text-gray-600"
              dangerouslySetInnerHTML={{ __html: t('detail.danger.confirmPrompt', { name: instance.name }) }}
            />
            <Input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={instance.name}
              invalid={Boolean(confirmName) && confirmName !== instance.name}
            />
            {deleteError && <Alert tone="danger">{deleteError}</Alert>}
          </div>
        </Modal>
      )}
    </div>
  )
}
